/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {getCVSPaginatedAssets} from '@ion/core/src/services/ArcGIS';
import {QueryParams} from '@ion/core/src/types/services';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {sortByKey} from '@ion/core/src/utils/ObjectUtil';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';

/**
 * Defines the parameters accepted by the hook.
 * params: Optional query parameters used for fetching asset IDs.
 * enabled: Boolean flag to enable or disable the query. Defaults to true.
 * staleTime: The duration (in milliseconds) for which the query data is considered fresh. 
 */
export interface UseLoadAssetIdParams {
  params?: QueryParams;
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number;
  isTrackingBeta?: boolean;
};

/**
 * Defines the results returned by the hook.
 * results: An array of asset IDs.
 * enabled: Boolean indicating if the query is enabled.
 */
export interface UseLoadAssetIdResults {
  results: number[];
  enabled: boolean;
};

/**
 * Custom React hook to load CVS asset IDs with support for caching and reactivity using react-query.
 * 
 * This hook utilizes the `react-query` library to manage the fetching, caching, and updating of asset IDs 
 * based on provided query parameters. It provides an easy-to-use interface for components to get 
 * asset IDs while handling caching and re-fetching logic automatically.
 * 
 * The hook takes in a configuration object with optional query parameters, an enabled state to control 
 * whether the query should be active, and a stale time to define how long the fetched data is considered fresh.
 * 
 * @param params    - Optional query parameters that are used to fetch asset IDs.
 * @param enabled   - Optional flag to enable or disable the query. Defaults to `true`.
 * @param staleTime - Optional time in milliseconds for which the data is considered fresh.
 *                    Defaults to the value from `getStaleTime`.
 * 
 * @returns {UseLoadAssetIdResults} An object containing the results (an array of asset IDs) and the enabled
 *                                  state of the query.
 * 
 * ```typescript
 * 
 *  import useLoadCVSAssetIds from '@ion/query/src/hooks/useLoadCVSAssetIds'; 
 * 
 *  const {results, enabled} = useLoadCVSAssetIds({params: {someQueryParam: 'value'}, enabled: true});
 * 
 *  useEffect(() => {
 *     if (enabled) {
 *      console.log('Fetched asset IDs:', results);
 *    }
 *  }, [results, enabled]);
 * 
 * ```
 */
export const useLoadCVSAssetIds = ({
  params,
  refetchInterval,
  enabled = true,
  staleTime = getStaleTime(5, false, false) as number,
  isTrackingBeta = false
}: UseLoadAssetIdParams): UseLoadAssetIdResults => {
  const keys = useMemo(() => (
    safeJSONStringify(sortByKey(createKey(QueryKey.GET_CVS_ASSETS_IDS, params)))
  ), [params]);

  const query = useQuery(keys, async() => {
    const results =  await getCVSPaginatedAssets({...params, outFields: 'assetId'}, isTrackingBeta);
    return results.map(({assetId}) => assetId);
  }, {staleTime, enabled, refetchInterval});

  return createQueryResult(query, {
    results : query.data || [],
    enabled
  }) as UseLoadAssetIdResults;
};

export default useLoadCVSAssetIds;
